import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { ApplogEntryDto, ApplogLevel } from 'src/api/FiretrolApiClient';
import { Text } from 'src/design/atoms/Text';
import {
  IUseTableProps,
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { ApplogLevelBadge } from 'src/routes/Settings/Applogs/components/ApplogLevelBadge';
import { formatApplogTimestamp } from 'src/routes/Settings/Applogs/applogFilters';

export interface IApplogsTableProps {
  entries: ApplogEntryDto[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: string;
  onRefresh: () => void;
  onSelect: (entry: ApplogEntryDto) => void;
}

const columns: MRT_ColumnDef<ApplogEntryDto>[] = [
  {
    accessorKey: 'timestamp',
    header: 'Timestamp',
    size: 200,
    Cell: ({ cell }) => formatApplogTimestamp(cell.getValue<Date>()),
  },
  {
    accessorKey: 'level',
    header: 'Level',
    size: 120,
    Cell: ({ cell }) => (
      <ApplogLevelBadge level={cell.getValue<ApplogLevel>()} />
    ),
  },
  {
    accessorKey: 'message',
    header: 'Message',
    size: 600,
    Cell: ({ cell }) => (
      <Text size="sm" lineClamp={1}>
        {cell.getValue<string>()}
      </Text>
    ),
  },
];

export const ApplogsTable: React.FC<IApplogsTableProps> = ({
  entries,
  isLoading,
  isFetching,
  isError,
  error,
  onRefresh,
  onSelect,
}) => {
  const tableState = useTableState({
    identifier: 'settings-app-logs',
    columns,
    defaultSorting: [{ id: 'timestamp', desc: true }],
    defaultPagination: { pageIndex: 0, pageSize: 25 },
  });

  const tableOptions = useMemo<IUseTableProps<ApplogEntryDto>['tableOptions']>(
    () => ({
      enableRowActions: false,
      enableClickToCopy: false,
      enableColumnFilters: false,
      columns,
      data: entries,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      localization: { noRecordsToDisplay: 'No log entries match' },
      // MRT runs this callback for its synthetic empty-state row too, whose
      // `original` is `{}` despite the DTO type.
      mantineTableBodyRowProps: ({ row }) =>
        row.original.message === undefined
          ? {}
          : {
              onClick: () => onSelect(row.original),
              style: { cursor: 'pointer' },
            },
    }),
    [entries, onSelect],
  );

  const { table } = useTable({
    ...tableState,
    tableOptions,
    totalCount: entries.length,
    isLoading,
    isFetching,
    isError,
    error,
    onRefresh,
  });

  return table;
};
